/**
 * drlh-video-warm.js
 * Warm the YouTube connection the moment a visitor shows intent to play, rather
 * than waiting for the click.
 *
 * Every video on the site is a click-to-load facade (hero, the rail, and
 * snippets/drlh-video-embed): the YouTube IFrame API script and the player
 * iframe are only fetched once the visitor clicks. That keeps YouTube off the
 * critical path for page load, but it means the FIRST click pays for four cold
 * cross-origin steps in series — DNS+TLS to youtube.com for the API script, the
 * API bootstrap, DNS+TLS to youtube-nocookie.com for the player, then the video
 * data. That is the "it takes a while" the reviewer felt.
 *
 * This warms all of that on the first sign of intent — a pointer moving over a
 * facade, a touch starting on one, or keyboard focus reaching one — which lands
 * ~100-300ms before the click completes. It opens the sockets (preconnect) and
 * starts the API script download, so by click time window.YT is usually ready
 * and the player's connection is already open. new YT.Player then mounts with
 * far less delay.
 *
 * Runs once per page and does nothing until intent, so unlike an unconditional
 * <head> preconnect it never competes with the LCP on a page where no video is
 * played (which is exactly why the theme does not preconnect YouTube globally).
 *
 * It cooperates with each facade's own loadYT(): the API <script> carries
 * id="drlh-yt-api", and every loadYT checks for that id before injecting, so the
 * script is fetched once whether warm() or a click gets there first.
 */
(function () {
  if (window.__drlhVideoWarm) return;
  window.__drlhVideoWarm = true;

  var warmed = false;
  // The three facade hooks: hero play button, rail card, embed wrapper.
  var SELECTOR = '[data-video-id], [data-inline-play], [data-drlh-vembed]';

  function preconnect(href) {
    var l = document.createElement('link');
    l.rel = 'preconnect';
    l.href = href;
    l.crossOrigin = '';
    document.head.appendChild(l);
  }

  function warm() {
    if (warmed) return;
    warmed = true;

    // Sockets: the player origin, the API-script origin, the thumbnail host, and
    // google.com (the player pings it). googlevideo.com (the media itself) is a
    // per-request wildcard subdomain that cannot be preconnected by exact host;
    // the player opens it once it knows the video, and the other warm sockets
    // shave enough that it is no longer the visible wait.
    preconnect('https://www.youtube-nocookie.com');
    preconnect('https://www.youtube.com');
    preconnect('https://i.ytimg.com');
    preconnect('https://www.google.com');

    // Start the IFrame API download now so window.YT is ready by the click.
    if (!document.getElementById('drlh-yt-api')) {
      var s = document.createElement('script');
      s.id = 'drlh-yt-api';
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  }

  function onIntent(e) {
    var t = e.target;
    if (t && t.closest && t.closest(SELECTOR)) warm();
  }

  // pointerover bubbles (pointerenter does not); touchstart is the mobile intent
  // signal, firing before the tap completes; focusin covers keyboard users.
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);

  // Proactive warm, AFTER the page has fully loaded and the main thread goes
  // idle. Hover only helps a visitor who hovers before clicking; many click the
  // play button directly, or hover and click in the same motion, and then the
  // socket and API script are still cold. Warming once the page is done means
  // the API is parsed and the YouTube sockets are open well before any click,
  // with zero cost to the initial render — it is deliberately gated behind BOTH
  // the load event and an idle callback so it never competes with LCP or FCP.
  //
  // This warms the connection and the API only. It does NOT mount a player or
  // request any video, so no YouTube poster, cookie, or video byte is fetched
  // until the visitor actually plays — the facade's privacy and bandwidth
  // posture is unchanged. Only runs on pages that actually have a video facade.
  function warmWhenIdle() {
    if (!document.querySelector(SELECTOR)) return;
    if ('requestIdleCallback' in window) {
      requestIdleCallback(warm, { timeout: 3000 });
    } else {
      setTimeout(warm, 1200);
    }
  }
  if (document.readyState === 'complete') {
    warmWhenIdle();
  } else {
    window.addEventListener('load', warmWhenIdle, { once: true });
  }
})();
